import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import SiteProofLogo from '../SiteProofLogo';

import UserProfileDropdown from './UserProfileDropdown';
import NotificationBell from './NotificationBell';
import { useAuth } from '../../contexts/AuthContext';
import { getNavItems, getDefaultDashboard } from '../../utils/roleAccess';
import { useDemoMode } from '../../hooks/useDemoMode';
import { useLanguage } from '../../contexts/LanguageContext';

const ROLE_BADGE_STYLES = {
  admin: 'bg-red-100 text-red-700 border-red-200',
  site_manager: 'bg-blue-100 text-blue-700 border-blue-200',
  worker: 'bg-green-100 text-green-700 border-green-200',
  client: 'bg-purple-100 text-purple-700 border-purple-200',
};

const ROLE_BADGE_LABELS = {
  admin: 'Admin',
  site_manager: 'Site Manager',
  worker: 'Worker',
  client: 'Client',
};

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, signOut, role } = useAuth();
  const { isDemoMode } = useDemoMode();
  const { t } = useLanguage();
  const navScrollRef = useRef(null);
  const activeTabRef = useRef(null);

  // Role-aware nav items
  const navItems = getNavItems(role);
  // Logo destination: demo users go back to landing, real users go to their dashboard
  const logoDest = isDemoMode ? '/landing-preview' : getDefaultDashboard(role);

  const isActivePath = (path) => {
    const current = location?.pathname;
    if (current === path) return true;
    // Match sub-routes (e.g. /sites/123 matches /sites nav item)
    if (path !== '/' && current?.startsWith(path + '/')) return true;
    return false;
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Check scroll state to show/hide chevrons
  const checkScrollState = useCallback(() => {
    const el = navScrollRef?.current;
    if (!el) return;
    setCanScrollLeft(el?.scrollLeft > 0);
    setCanScrollRight(el?.scrollLeft + el?.clientWidth < el?.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = navScrollRef?.current;
    if (!el) return;
    checkScrollState();
    el?.addEventListener('scroll', checkScrollState);
    const ro = new ResizeObserver(checkScrollState);
    ro?.observe(el);
    return () => {
      el?.removeEventListener('scroll', checkScrollState);
      ro?.disconnect();
    };
  }, [checkScrollState]);

  // Scroll active tab into view on route change
  useEffect(() => {
    if (activeTabRef?.current) {
      activeTabRef?.current?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
    // Re-check scroll state after navigation
    setTimeout(checkScrollState, 100);
  }, [location?.pathname, checkScrollState]);

  // Wheel → horizontal scroll on tab bar
  useEffect(() => {
    const el = navScrollRef?.current;
    if (!el) return;
    const onWheel = (e) => {
      if (e?.deltaY === 0) return;
      e?.preventDefault();
      el.scrollLeft += e?.deltaY;
    };
    el?.addEventListener('wheel', onWheel, { passive: false });
    return () => el?.removeEventListener('wheel', onWheel);
  }, []);

  const scrollNav = (dir) => {
    const el = navScrollRef?.current;
    if (!el) return;
    el?.scrollBy({ left: dir * 200, behavior: 'smooth' });
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* Demo mode banner */}
      {isDemoMode && (
        <div className="w-full bg-blue-600 text-white text-xs flex items-center justify-center gap-3 px-4 py-1.5">
          <span className="font-medium">{t('viewingLiveDemo')}</span>
          <span className="text-blue-300">·</span>
          <button
            onClick={async () => { await signOut(); window.location.href = '/demo-access'; }}
            className="underline text-white hover:text-blue-100 font-medium transition-colors bg-transparent border-none p-0 cursor-pointer text-xs"
          >
            Switch role
          </button>
          <span className="text-blue-300">·</span>
          <button
            onClick={async () => { await signOut(); window.location.href = 'https://site-proof.app'; }}
            className="underline text-white hover:text-blue-100 font-medium transition-colors bg-transparent border-none p-0 cursor-pointer text-xs"
          >
            {t('backToWebsite')}
          </button>
        </div>
      )}
      {/* Main header row */}
      <div className="flex flex-row items-center min-h-[56px] px-4 gap-3 w-full">

        {/* Logo — routes to role dashboard */}
        <Link
          to={logoDest}
          className="flex items-center gap-2 flex-shrink-0 no-underline"
        >
          <SiteProofLogo size="sm" />
        </Link>

        {/* Role badge */}
        {role && (
          <span className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${ROLE_BADGE_STYLES?.[role] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
            {t(ROLE_BADGE_LABELS?.[role]) || ROLE_BADGE_LABELS?.[role] || role}
          </span>
        )}

        {/* ===== NAV TABS — middle, flex-1 ===== */}
        <div className="hidden md:flex flex-1 min-w-0 items-center overflow-hidden">
          {/* Left chevron — desktop only */}
          {canScrollLeft && (
            <button
              onClick={() => scrollNav(-1)}
              className="hidden md:flex flex-shrink-0 items-center justify-center w-9 h-9 mr-1 rounded-full bg-white text-gray-700 hover:text-gray-900 hover:bg-gray-50 shadow-sm ring-1 ring-gray-200 hover:ring-gray-300 transition-all"
              aria-label="Scroll tabs left"
            >
              <Icon name="ChevronLeft" size={20} />
            </button>
          )}

          {/* Scrollable tabs container */}
          <div
            ref={navScrollRef}
            className="sp-nav flex-1 overflow-x-auto overflow-y-visible"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {/* Hide webkit scrollbar */}
            <style>{`.sp-nav::-webkit-scrollbar { display: none; }`}</style>
            <div
              className="flex flex-nowrap items-end h-[56px]"
              style={{ minWidth: 'max-content' }}
            >
              {navItems?.map((item) => {
                const active = isActivePath(item?.path);
                return (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    ref={active ? activeTabRef : null}
                    className={[
                      'flex-shrink-0 inline-flex items-center gap-1.5 px-4 h-[48px] text-sm font-medium whitespace-nowrap border-b-2 transition-colors min-w-[80px] justify-center',
                      active
                        ? 'border-blue-600 text-blue-600 bg-blue-50' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50',
                    ]?.join(' ')}
                    style={{ textDecoration: 'none' }}
                  >
                    <span className="hidden md:inline-flex"><Icon name={item?.icon} size={15} /></span>
                    <span>{t(item?.label)}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Right chevron — desktop only */}
          {canScrollRight && (
            <button
              onClick={() => scrollNav(1)}
              className="hidden md:flex flex-shrink-0 items-center justify-center w-9 h-9 ml-1 rounded-full bg-white text-gray-700 hover:text-gray-900 hover:bg-gray-50 shadow-sm ring-1 ring-gray-200 hover:ring-gray-300 transition-all"
              aria-label="Scroll tabs right"
            >
              <Icon name="ChevronRight" size={20} />
            </button>
          )}
        </div>
        {/* ===== END NAV TABS ===== */}

        {/* Right actions — fixed width */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <NotificationBell />
          <UserProfileDropdown />
          <button
            onClick={toggleMobileMenu}
            className="md:hidden flex items-center justify-center w-9 h-9 rounded-md border-none bg-transparent cursor-pointer hover:bg-gray-100 transition-colors"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
          </button>
        </div>
      </div>
      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="bg-white border-t border-gray-200 max-h-[calc(100vh-56px)] overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom,0px)]">
          <nav className="flex flex-col p-4 gap-2">
            {navItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={[
                  'flex items-center gap-3 px-4 py-3 rounded-md font-medium no-underline transition-colors',
                  isActivePath(item?.path)
                    ? 'bg-blue-50 text-blue-700' :'text-gray-700 hover:bg-gray-50',
                ]?.join(' ')}
              >
                <Icon name={item?.icon} size={20} />
                <span>{t(item?.label)}</span>
              </Link>
            ))}
            {/* Language selector hidden until translations are complete */}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;